import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createRbacMember,
  createRbacRole,
  deleteRbacMember,
  deleteRbacRole,
  getRbacMembers,
  getRbacPermissions,
  getRbacRoles,
  setRbacMemberRoles,
  setRbacRolePermissions,
  toggleRbacMemberActive,
  updateRbacRole,
} from "../../../services";
import {
  SupplierRbacMember,
  SupplierRbacPermission,
  SupplierRbacRole,
} from "../../../privatePages/supplier/types";
import { QUERY_KEYS } from "../queryKeys";

export const useGetRbacPermissions = () => {
  return useQuery<SupplierRbacPermission[], Error>({
    queryKey: [QUERY_KEYS.GET_RBAC_PERMISSIONS],
    queryFn: () => getRbacPermissions(),
    refetchOnWindowFocus: false,
  });
};

export const useGetRbacRoles = () => {
  return useQuery<SupplierRbacRole[], Error>({
    queryKey: [QUERY_KEYS.GET_RBAC_ROLES],
    queryFn: () => getRbacRoles(),
    refetchOnWindowFocus: false,
  });
};

export const useGetRbacMembers = () => {
  return useQuery<SupplierRbacMember[], Error>({
    queryKey: [QUERY_KEYS.GET_RBAC_MEMBERS],
    queryFn: () => getRbacMembers(),
    refetchOnWindowFocus: false,
  });
};

export const useCreateRbacRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { name: string; description?: string }) =>
      createRbacRole(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_ROLES],
      });
    },
  });
};

export const useUpdateRbacRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      roleId,
      data,
    }: {
      roleId: number;
      data: {
        name?: string;
        description?: string;
      };
    }) => updateRbacRole(roleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_ROLES],
      });
    },
  });
};

export const useDeleteRbacRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId: number) => deleteRbacRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_ROLES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_MEMBERS],
      });
    },
  });
};

export const useSetRbacRolePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      roleId,
      permission_ids,
    }: {
      roleId: number;
      permission_ids: number[];
    }) => setRbacRolePermissions(roleId, permission_ids),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_ROLES],
      });
    },
  });
};

export const useCreateRbacMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      password: string;
      cedula?: string;
      role_ids: number[];
    }) => createRbacMember(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_MEMBERS],
      });
    },
  });
};

export const useToggleRbacMemberActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      is_active,
    }: {
      memberId: number;
      is_active: boolean;
    }) => toggleRbacMemberActive(memberId, is_active),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_MEMBERS],
      });
    },
  });
};

export const useDeleteRbacMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: number) => deleteRbacMember(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_MEMBERS],
      });
    },
  });
};

export const useSetRbacMemberRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      role_ids,
    }: {
      memberId: number;
      role_ids: number[];
    }) => setRbacMemberRoles(memberId, role_ids),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RBAC_MEMBERS],
      });
    },
  });
};
